import {
  CopyTask,
  getTranslatedText,
  Icon,
  NewTask,
  BackgroundIcon,
  isAuthorised,
  AccessControl,
  TEXT_LENGTH
} from '@nile/components';
import {
  Button,
  ButtonGroup,
  Collapsible,
  TextField,
  Tooltip
} from '@superops/ui-design-system';
import { Medal, Trash } from '@styled-icons/fa-solid';
import { KeyboardArrowDown } from '@styled-icons/material-twotone/KeyboardArrowDown';
import { KeyboardArrowUp } from '@styled-icons/material-twotone/KeyboardArrowUp';
import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo
} from 'react';
import styled from 'styled-components';
import { constructTaskObject } from '@nile/projects/src/Utils';
import envConfig from '@nile/home/src/Config/env';
import TaskCard from './TaskStrip';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { sortBy } from 'lodash';

const CardWrapper = styled.div`
  .card-border {
    border: 1px solid #0000001a;
  }
  .br-10 {
    border-radius: 10px;
  }
  .mx--20 {
    margin-left: -20px;
    margin-right: -20px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  img {
    width: 100px;
    height: 100px;
  }
  .text {
    font-weight: 400;
    color: #000000cc;
  }
`;

const OuterWrapper = styled.div`
  display: flex;
  flex: 1 1;
  align-items: end;

  &.secondCol {
    justify-content: flex-end;
    flex: 1 1;
  }

  .toggleBtn {
    color: #00000066;
  }
`;

const MilestoneCard = (props) => {
  const {
    intl,
    module,
    hideSectionsInTaskForm = [],
    isTemplate,
    getSelectedTaskNameList,
    onTasksReorder,
    disableDrag,
    tasks
  } = props;
  const [active, setActive] = useState(true);
  const [title, setTitle] = useState(props?.title || '');
  const [error, setErrors] = useState(props?.errors || null);
  const [sheetActive, setSheetActive] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (ref.current && !props?.preventScroll) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  useEffect(() => setErrors(props?.errors), [props.errors]);

  const filterAttrs = (obj) => {
    const { name, ...rest } = obj;
    return rest;
  };

  const updateContext = (type, tasks) => {
    if (type === 'taskTemplateList') {
      const updatedTaskList = tasks.map((taskTemp) => {
        if (props.sendAllKeysInTemplate) {
          return {
            ...constructTaskObject(taskTemp, hideSectionsInTaskForm),
            taskTemplateId: taskTemp.taskTemplateId
          };
        }
        const { taskTemplateId, ...rest } = taskTemp;
        if (rest.technician) {
          rest.technician = filterAttrs(rest.technician);
        }
        return rest;
      });
      const newObj = {
        milestoneId: props.milestoneId,
        name: title,
        addTasks: [...props.tasks, ...updatedTaskList]
      };
      props.updateMilestone(props.index, newObj);
    }
  };

  const context = {
    value: {
      addTaskTemplateList: [],
      removeTaskTemplateList: [],
      taskTemplateList: [],
      selectedTaskTemplates: []
    },
    updateContext: updateContext
  };

  const modifiedTaskList =
    props.taskTemplates?.getTaskTemplateList?.map((task) => ({
      ...task,
      id: task.taskTemplateId,
      name: task.title,
      label: task.title
    })) || [];

  const tasksList = getSelectedTaskNameList
    ? modifiedTaskList?.map((task) => {
        return {
          ...task,
          disable: getSelectedTaskNameList().includes(task.title)
        };
      }) || []
    : modifiedTaskList;

  const handleToggle = useCallback(() => setActive((val) => !val), []);

  const handleChange = (val) => {
    setErrors(null);
    setTitle(val);
  };

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };

  const handleUpdation = () => {
    const newObj = {
      milestoneId: props.milestoneId,
      name: title,
      addTasks: [...props.tasks]
    };
    props.updateMilestone(props.index, newObj);
  };

  const handleDeletion = () => {
    props.deleteMilestone(props.index);
  };

  const handleTaskAddition = (type, task) => {
    const taskObj = constructTaskObject(task, hideSectionsInTaskForm);
    const newObj = {
      milestoneId: props.milestoneId,
      name: title,
      addTasks: [...props.tasks]
    };
    newObj.addTasks.push(taskObj);
    props.updateMilestone(props.index, newObj);
    setSheetActive(!sheetActive);
  };

  const handleTaskDeletion = (ind) => {
    const { tasks } = props;
    const updatedTasks = [...tasks].filter((obj, i) => i !== ind);
    const newObj = {
      milestoneId: props.milestoneId,
      name: title,
      addTasks: updatedTasks
    };
    props.updateMilestone(props.index, newObj);
  };

  const handleTaskUpdation = (ind, task) => {
    const taskObj = constructTaskObject(task, hideSectionsInTaskForm);
    const { tasks } = props;
    const updatedTasks = [...tasks];
    updatedTasks[ind] = {
      ...taskObj,
      taskTemplateId: props.sendAllKeysInTemplate
        ? updatedTasks[ind].taskTemplateId
        : undefined
    };
    const newObj = {
      milestoneId: props.milestoneId,
      name: title,
      addTasks: updatedTasks
    };
    props.updateMilestone(props.index, newObj);
  };

  return (
    <CardWrapper className={active ? 'expanded' : ''} ref={ref}>
      <div className="card-border p20 br-10 mb-20 mx--20 visibilityContainer">
        <div className="fx" data-qa="milestoneCard">
          <OuterWrapper className="mlCard">
            {!disableDrag ? (
              <SuperSortable.DragHandle className="visibilityActions self-center" />
            ) : (
              <div className="pl-15" />
            )}
            <div className="opacity5">
              <SVGIcon name="Milestone" size="lg" color="#323232" />
            </div>
            <div className="ml-10">
              <Tooltip
                content={
                  <div className="tooltip">
                    {title ||
                      getTranslatedText(
                        intl,
                        'ticket.newProject.untitledMilestone'
                      )}
                  </div>
                }
              >
                <TextField
                  placeholder={getTranslatedText(
                    intl,
                    'ticket.newProject.untitledMilestone'
                  )}
                  value={title}
                  onChange={handleChange}
                  onBlur={handleUpdation}
                  error={error}
                  customClassName="ellipsis"
                  maxLength={TEXT_LENGTH.size_5}
                />
              </Tooltip>
            </div>
          </OuterWrapper>
          <OuterWrapper className="flex actr secondCol">
            <ButtonGroup>
              <div className="visibilityActions deleteBtn">
                <Button
                  onClick={handleDeletion}
                  icon={
                    <div className="opacity5">
                      <SVGIcon name="Delete" />
                    </div>
                  }
                />
              </div>
              <div className="visibilityActions" data-qa="createMilestoneTask">
                <NewTask
                  addOrUpdateTechGroup={handleTaskAddition}
                  state={sheetActive}
                  toggleSheetActive={toggleSheetActive}
                  name={getTranslatedText(intl, 'ticket.newProject.createTask')}
                  hideIcon
                  module={module || 'PROJECT'}
                  hideSections={hideSectionsInTaskForm}
                  details={{ operation: 'CREATE' }}
                  isTemplate={isTemplate}
                  icon={
                    <div className="opacity5">
                      <SVGIcon name="Tasks" />
                    </div>
                  }
                />
              </div>
              <AccessControl
                roleToCheck="taskTemplate.view"
                render={
                  <div
                    className="visibilityActions"
                    data-qa="createMilestoneTaskTemplate"
                  >
                    <CopyTask
                      tasksList={tasksList}
                      context={context}
                      name={getTranslatedText(
                        intl,
                        'ticket.newProject.taskTemp'
                      )}
                      hideSectionsInTask={hideSectionsInTaskForm}
                      sendAll={props.sendAllKeysInTemplate}
                      usePropTemplates={!!getSelectedTaskNameList}
                      icon={
                        <div className="opacity5">
                          <SVGIcon name="Tasks" />
                        </div>
                      }
                      hideIcon
                    />
                  </div>
                }
              />
            </ButtonGroup>
            <div className="toggleBtn ml-10">
              <Button
                onClick={handleToggle}
                plain
                icon={
                  active ? (
                    <SVGIcon name="ArrowDown" size="lg" />
                  ) : (
                    <SVGIcon name="ArrowUp" size="lg" />
                  )
                }
              />
            </div>
          </OuterWrapper>
        </div>
        <Collapsible
          open={active}
          transition={{ duration: '150ms', timingFunction: 'ease' }}
        >
          {tasks?.length > 0 ? (
            <SuperSortable
              onDragEnd={(list) => {
                const newList = list.map((task, index) => ({
                  ...task,
                  taskOrder: index + 1
                }));

                onTasksReorder(newList);
              }}
              list={tasks}
            >
              {tasks.map((task, index) => (
                <TaskCard
                  task={task}
                  handleTaskDeletion={handleTaskDeletion}
                  handleTaskUpdation={handleTaskUpdation}
                  index={index}
                  module={module}
                  hideSectionsInTaskForm={hideSectionsInTaskForm}
                  isTemplate={isTemplate}
                  enableDrag
                />
              ))}
            </SuperSortable>
          ) : (
            <ImageContainer>
              <img
                className="pt25"
                src={`${envConfig.imageRoot}svg/EmptyTask.svg`}
                alt="Empty Tasks"
              />
              <div className="text">
                <FormattedMessage id="ticket.newProject.noTask" />
              </div>
            </ImageContainer>
          )}
        </Collapsible>
      </div>
    </CardWrapper>
  );
};

export default injectIntl(MilestoneCard);
